/**
 * 25 Oct 2026 (D-1025-TODAY) — a named attention entry in words, in the reader's language: "TEST-04C · Name · Thu, Nov 12
 * visit not confirmed". The person or thing comes first, then what is wanted; a line the API sent as a count alone (a
 * viewer's line, a ticket count, the inbox) is the count's sentence it always was.
 */
import type { TFunction } from 'i18next';
import type { HouseAttnEntry } from '@shared/house-today';
import { activityWords, type CategoryKey, type TripKind } from '@shared/volunteers';
import { AGREEMENT_TITLE, isCardAgreement } from '@shared/agreement-cards';
import { formatClockOf, formatDayKey } from '@/lib/dates';
import { companionItemWords } from '@/features/volunteers/companionItems';
import { attnWords } from './words';

const join = (...parts: (string | null | undefined)[]) => parts.filter((p): p is string => !!p).join(' · ');

export function entryWords(e: HouseAttnEntry, t: TFunction, lang: string): string {
  const { line, item } = e;
  if (!item) return attnWords(line, t);
  if (item.companion) return companionItemWords(item.companion, t, lang)?.text ?? item.companion.text;
  const day = item.dayKey ? formatDayKey(item.dayKey, lang) : '';
  const who = item.who ?? item.label;
  switch (line.kind) {
    case 'reachOut':
      return join(who, t('today1025.item.reachOut', { day }));
    case 'pendingCloseout':
      return join(who, t('today1025.item.pendingCloseout', { day }));
    case 'hold':
      return join(who, item.until ? t('today1025.item.hold', { day, time: formatClockOf(item.until, lang) }) : t('today1025.item.holdNoEnd', { day }));
    case 'needsNewDay':
      return join(who, item.dayKey ? t('today1025.item.needsNewDayFrom', { day }) : t('today1025.item.needsNewDay'));
    case 'incident':
      return join(who, t('today1025.item.incident', { day }));
    case 'complaintFollowUp':
      return join(who, t('today1025.item.complaintFollowUp', { day }));
    case 'agreementOpened': {
      const lg = lang === 'es' ? 'es' : 'en';
      const agreement = item.what && isCardAgreement(item.what) ? AGREEMENT_TITLE[item.what][lg] : '';
      return t('today1101.item.agreementOpened', { name: item.label ?? '', agreement, day });
    }
    case 'outingNotSent':
      return join(item.label, t('today1025.item.outingNotSent', { day }));
    case 'untaken': {
      const a = item.activity;
      const activity = a ? activityWords(a.category as CategoryKey, (a.trip ?? null) as TripKind | null, lang === 'es' ? 'es' : 'en', a.otherWhat) : '';
      return join(item.who, t('today1025.item.untaken', { activity, day }));
    }
    case 'shortVolunteers': {
      const need = item.need ?? 0;
      const have = item.have ?? 0;
      return join(item.label, t('today1025.item.shortVolunteers', { count: Math.max(need - have, 0), have, need }));
    }
    case 'newVolunteer':
      return join(item.label, t('today1025.item.newVolunteer'));
    case 'volunteerNotice':
      return join(item.label, t('today1025.item.volunteerNotice'));
    case 'more': {
      const said = item.what ? t(`today1025.more_what.${item.what}`, { day, defaultValue: '' }) : '';
      return join(item.who, said || t('today1025.more_what.other'));
    }
    default:
      return attnWords(line, t);
  }
}
